import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { LogOut, ShoppingBag, FileText, Settings, Coins, Users, Heart, Trophy, Loader2 } from "lucide-react";
import PurchasesTab from "./PurchasesTab";
import CustomerSettingsTab from "./CustomerSettingsTab";
import "../dashboard.css";

const CustomerDashboard = ({ logout }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("browse");
  const [marketData, setMarketData] = useState(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState("");
  const [purchasing, setPurchasing] = useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      fetchMarketData();
      
      // Check if returning from Stripe
      const urlParams = new URLSearchParams(window.location.search);
      const paymentSuccess = urlParams.get('payment_success');
      const paymentCancelled = urlParams.get('payment_cancelled');
      
      if (paymentSuccess === 'true') {
        toast.success("Payment successful! Your tokens have been purchased.");
        setActiveTab("purchases");
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (paymentCancelled === 'true') {
        toast.info("Payment was cancelled. You can try again anytime.");
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      if (response.data.role !== 'customer') {
        navigate("/login");
      }
    } catch (error) {
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketData = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/available-tokens`);
      setMarketData(response.data);
    } catch (error) {
      console.error("Failed to fetch market data:", error);
    }
  };

  const handlePurchase = async () => {
    const qty = parseInt(purchaseQuantity);
    
    if (!qty || qty <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }
    
    if (marketData?.minimum_purchase && qty < marketData.minimum_purchase) {
      toast.error(`Minimum purchase is ${marketData.minimum_purchase} tokens`);
      return;
    }
    
    if (qty > marketData?.total_tokens) {
      toast.error(`Only ${marketData.total_tokens} tokens available`);
      return;
    }
    
    setPurchasing(true);
    try {
      const response = await axios.post(`${API}/marketplace/purchase-tokens`, {
        quantity: qty
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Purchase failed");
    } finally {
      setPurchasing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: marketData?.currency || 'GBP'
    }).format(amount);
  };

  return (
    <div className="dashboard-container gradient-blue">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              {user?.company_logo ? (
                <img
                  src={user.company_logo}
                  alt={user.company_name}
                  className="h-12 w-12 object-contain rounded-lg border border-gray-200"
                />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-lg border-2 border-blue-700">
                  {user?.company_name?.substring(0, 2).toUpperCase() || "CO"}
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-gray-800" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                  {user?.company_name || "Customer Dashboard"}
                </h1>
                <p className="text-xs text-gray-600">
                  {user?.first_name} {user?.last_name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_volunteer-hub-35/artifacts/5ta1s578_playback%20logo.jpg"
                  alt="PlayBack Token"
                  className="h-8 w-8 object-contain"
                />
                <span className="text-xs font-semibold text-red-600 hidden sm:inline" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                  PlayBack Token
                </span>
              </div>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-red-500 text-red-600 hover:bg-red-50 btn-modern"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="modern-tabs-list grid grid-cols-3 mb-4">
            <TabsTrigger value="browse" data-testid="tab-browse" className="modern-tab-trigger">
              <ShoppingBag className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Buy Tokens</span>
              <span className="sm:hidden">Buy</span>
            </TabsTrigger>
            <TabsTrigger value="purchases" data-testid="tab-purchases" className="modern-tab-trigger">
              <FileText className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Purchases</span>
              <span className="sm:hidden">Orders</span>
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="modern-tab-trigger">
              <Settings className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Settings</span>
              <span className="sm:hidden">Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="browse">
            <div className="space-y-6">
              {/* Hero Section */}
              <Card className="bg-gradient-to-r from-blue-600 to-blue-800 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                      <h2 className="text-2xl font-bold mb-2">Available Volunteer Tokens</h2>
                      <p className="text-blue-100">
                        Support UK community organisations and boost your ESG credentials
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-4xl font-bold">{marketData?.total_tokens || 0}</div>
                      <div className="text-blue-200 text-sm">tokens available</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-l-4 border-l-green-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-green-500" />
                      Sports Clubs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {marketData?.by_club_type?.sports || 0}
                    </div>
                    <p className="text-xs text-gray-500">volunteer hours</p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-blue-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-500" />
                      Community Groups
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {marketData?.by_club_type?.community || 0}
                    </div>
                    <p className="text-xs text-gray-500">volunteer hours</p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-pink-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                      <Heart className="w-4 h-4 text-pink-500" />
                      Charities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-pink-600">
                      {marketData?.by_club_type?.charity || 0}
                    </div>
                    <p className="text-xs text-gray-500">volunteer hours</p>
                  </CardContent>
                </Card>
              </div>

              {/* Activity Types Breakdown */}
              {marketData?.by_activity_type && Object.keys(marketData.by_activity_type).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Activity Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      {Object.entries(marketData.by_activity_type).map(([activity, hours]) => (
                        <div key={activity} className="bg-gray-50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-gray-800">{hours}</div>
                          <div className="text-xs text-gray-500 capitalize">{activity}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Purchase Section */}
              <Card className="border-2 border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-blue-600" />
                    Purchase Tokens
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Price per token:</span>
                      <span className="text-xl font-bold text-blue-600">
                        {formatCurrency(marketData?.token_price || 5)}
                      </span>
                    </div>
                    {marketData?.minimum_purchase && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Your minimum purchase:</span>
                        <span className="font-semibold text-orange-600">
                          {marketData.minimum_purchase} tokens
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quantity">Number of tokens to purchase</Label>
                    <div className="flex gap-3">
                      <Input
                        id="quantity"
                        type="number"
                        min={marketData?.minimum_purchase || 1}
                        max={marketData?.total_tokens || 0}
                        value={purchaseQuantity}
                        onChange={(e) => setPurchaseQuantity(e.target.value)}
                        placeholder={`Enter quantity ${marketData?.minimum_purchase ? `(min: ${marketData.minimum_purchase})` : ''}`}
                        className="flex-1"
                      />
                      <Button
                        onClick={() => setShowPurchaseDialog(true)}
                        disabled={!purchaseQuantity || parseInt(purchaseQuantity) <= 0 || purchasing}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                      >
                        {purchasing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Buy Now"
                        )}
                      </Button>
                    </div>
                  </div>

                  {purchaseQuantity && parseInt(purchaseQuantity) > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4 mt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Total:</span>
                        <span className="text-2xl font-bold text-green-600">
                          {formatCurrency(parseInt(purchaseQuantity) * (marketData?.token_price || 5))}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="purchases">
            <PurchasesTab user={user} />
          </TabsContent>

          <TabsContent value="settings">
            <CustomerSettingsTab user={user} onUpdate={fetchUser} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Purchase Confirmation Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Purchase</DialogTitle>
            <DialogDescription>
              Review your order before proceeding to payment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Tokens:</span>
                <span className="font-semibold">{purchaseQuantity}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Price per token:</span>
                <span className="font-semibold">{formatCurrency(marketData?.token_price || 5)}</span>
              </div>
              <hr />
              <div className="flex justify-between text-lg">
                <span className="font-semibold">Total:</span>
                <span className="font-bold text-green-600">
                  {formatCurrency(parseInt(purchaseQuantity || 0) * (marketData?.token_price || 5))}
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-500">
              Tokens will be allocated from the oldest verified volunteer hours first.
              You&apos;ll receive an ESG report upon successful purchase.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowPurchaseDialog(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowPurchaseDialog(false);
                handlePurchase();
              }}
              disabled={purchasing}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {purchasing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Proceed to Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDashboard;
